// src/pages/Presentation/OpenViewer.tsx
import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SmartVideoPlayerGallery from '../Gallery/SmartVideoPlayerGallery';

type MediaEntry = {
  url?: string;
  videoId?: string | null;
  isHls?: boolean;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL!;
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const isVideoEntry = (m?: MediaEntry | null) => {
  if (!m) return false;
  return !!(
    m.isHls ||
    m.videoId ||
    m.hlsUrl ||
    m.dashUrl ||
    (m.url && /\.m3u8($|\?)/i.test(m.url))
  );
};

const resolveVideoUrl = (m: MediaEntry) => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  if (m.url) return m.url;
  return '';
};

const resolvePosterUrl = (m: MediaEntry) => {
  if (m.posterUrl) return m.posterUrl;
  if (m.videoId) return `${API_ORIGIN}/thumbnails/${m.videoId}.jpg`;
  return undefined;
};

const OpenViewer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  
  const { media = [], startIndex = 0 } = (location.state || {}) as {
    media: MediaEntry[];
    startIndex?: number;
  };
const [dragX, setDragX] = useState(0);

  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef<number | null>(null);

  const current = media[index];

  const isVideo = isVideoEntry(current);
  const isImage = !!current?.url && !isVideo;

  const goNext = () => {
  document.querySelectorAll('video').forEach(v => v.pause());
  if (index < media.length - 1) setIndex(i => i + 1);
};

  const goPrev = () => {
      document.querySelectorAll('video').forEach(v => v.pause());
    if (index > 0) setIndex(i => i - 1);
  };

 const onTouchStart = (e: React.TouchEvent) => {
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'VIDEO') return; // allow tap to control video
  touchStartX.current = e.touches[0].clientX;
};
const onTouchMove = (e: React.TouchEvent) => {
  if (touchStartX.current === null) return;
  const dx = e.touches[0].clientX - touchStartX.current;

  const resistance =
    (index === 0 && dx > 0) || (index === media.length - 1 && dx < 0)
      ? 0.25
      : 1;

  setDragX(dx * resistance);
};



 const onTouchEnd = () => {
  if (touchStartX.current === null) return;

const SWIPE_THRESHOLD = 60;

if (dragX > SWIPE_THRESHOLD) goPrev();
else if (dragX < -SWIPE_THRESHOLD) goNext();
  setDragX(0);
  touchStartX.current = null;
};


React.useEffect(() => {
  const vids = document.querySelectorAll('video');
  vids.forEach(v => {
    try {
      v.pause();
      v.currentTime = 0;
    } catch {}
  });
}, [index]);

React.useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
    // ignore typing in inputs / textareas
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      navigate(-1);
    }
  };

  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [index, media.length]);



  if (!current) {
    return (
      <div style={emptyStyle}>
        No media
        <button onClick={() => navigate(-1)}>Close</button>
      </div>
    );
  }

  return (
    <div
  style={viewerStyle}
  onTouchStart={onTouchStart}
onTouchMove={onTouchMove}
onTouchEnd={onTouchEnd}
>

   
      {/* Close */}
      <button style={closeBtn} onClick={() => navigate(-1)}>
        ✕
      </button>

      {/* Media */}
<div
  style={{
    ...mediaFrame,
    transform: `translateX(${dragX}px)`,
    transition: dragX === 0 ? 'transform 0.25s ease' : 'none',
  }}
>
{isImage ? (
  <img
    key={current.url}
    src={current.url}
    draggable={false}
    style={{
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'cover',
      display: 'block',
    }}
  />
) : isVideo ? (
  <SmartVideoPlayerGallery
  key={current.videoId || current.hlsUrl || current.url || `video-${index}`}
  videoUrl={resolveVideoUrl(current)}
  poster={resolvePosterUrl(current)}
  muted={false}
  showControls={false}
  clickToToggle
  preload="none"
  musicId={current.musicId}
  musicTitle={current.musicTitle}
  musicArtist={current.musicArtist}
  style={mediaStyle}
/>

) : null}

      </div>

      {/* Pagination dots */}
      <div style={dotsWrap}>
        {media.map((_, i) => (
          <div
            key={i}
            style={{
              ...dot,
              opacity: i === index ? 1 : 0.3,
              transform: i === index ? 'scale(1.3)' : 'scale(1)',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default OpenViewer;
const viewerStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#000',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  touchAction: 'pan-y',
  zIndex: 999999,
};

const mediaFrame: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1, // keep media below UI
};


const mediaStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor: '#000',
  display: 'block',
};


const closeBtn: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 14,
  width: 44,
  height: 44,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: '#cebaba',
  fontSize: 22,
  cursor: 'pointer',
  zIndex: 9999999,   // ✅ ADD THIS
  backdropFilter: 'blur(6px)', // optional premium touch
};




const dotsWrap: React.CSSProperties = {
  position: 'absolute',
  bottom: 18,
  display: 'flex',
  gap: 8,
  zIndex: 5,
};

const dot: React.CSSProperties = {
  width: 4,
  height: 4,
  borderRadius: '50%',
  background: '#897e7e',
  transition: 'all 0.2s ease',
};

const emptyStyle: React.CSSProperties = {
  color: '#b9afaf',
  fontSize: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  justifyContent: 'center',
  alignItems: 'center',
};
